import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import Swal from "sweetalert2";
import { getUserPermissions } from "../core/api/auth";
import { setPermissions } from "../core/redux/action";
import Loader from "../feature-module/loader/loader";
import { orderOfPage } from "./all_routes";

/**
 * PermissionRoute component
 * Checks if the user has permission to access the requested route
 * Fetches user permissions if they haven't been loaded yet
 */
const EMPTY_PERMISSIONS = { loaded: false };

const PermissionRoute = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.auth, shallowEqual);
  const permissionsState = useSelector(
    (state) => state.permissions,
    shallowEqual
  );

  const isAuthenticated = authState?.isAuthenticated;
  const permissions = permissionsState ?? EMPTY_PERMISSIONS;
  // Initialize loading to false to ensure we can trigger the API call
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Store the previous path for navigation fallback
  const [previousPath, setPreviousPath] = useState("/");

  // Store the current path when it changes
  useEffect(() => {
    if (location.pathname && isAuthenticated && permissions.loaded) {
      // Only update previous path if user has permission for current path
      const hasPermission = checkPermissionForPath(
        location.pathname,
        permissions
      );
      if (hasPermission) {
        console.log(
          `[PermissionRoute] Storing previous path: ${location.pathname}`
        );
        setPreviousPath(location.pathname);
      }
    }
  }, [location.pathname, isAuthenticated, permissions]);

  // Helper function to check if user has permission for a specific path
  const checkPermissionForPath = (path, permissions) => {
    // Super admin or user with all permissions can access everything
    if (permissions.hasAllPermissions || permissions.role === "super_admin") {
      return true;
    }

    // Check in page_permissions
    let hasPermissionFromPagePermissions = false;
    if (
      permissions.page_permissions &&
      Object.keys(permissions.page_permissions).length > 0
    ) {
      hasPermissionFromPagePermissions = Object.keys(
        permissions.page_permissions
      ).some((route) => {
        // Exact match
        if (route === path) {
          return true;
        }

        // Check if the path starts with a permitted route (for nested routes)
        if (path.startsWith(route)) {
          return true;
        }

        return false;
      });
    }

    // Check in routes array
    let hasPermissionFromRoutes = false;
    if (permissions.routes && permissions.routes.length > 0) {
      hasPermissionFromRoutes = permissions.routes.some((route) => {
        // Exact match
        if (route === path) {
          return true;
        }

        // Check if the path starts with a permitted route (for nested routes)
        if (path.startsWith(route)) {
          return true;
        }

        return false;
      });
    }

    return hasPermissionFromPagePermissions || hasPermissionFromRoutes;
  };

  // Fetch user permissions if they haven't been loaded yet
  useEffect(() => {
    console.log("[PermissionRoute] useEffect triggered with:", {
      isAuthenticated,
      permissionsLoaded: permissions.loaded,
      loading,
      token: localStorage.getItem("accessToken") ? "exists" : "missing",
    });

    // Always fetch permissions if authenticated and not already loaded
    if (isAuthenticated && !permissions.loaded && !loading) {
      console.log(
        "[PermissionRoute] Conditions met, fetching user permissions..."
      );
      setLoading(true);

      getUserPermissions()
        .then((data) => {
          console.log("[PermissionRoute] Permissions received:", {
            role: data.role,
            routesCount: data.routes?.length || 0,
            hasAllPermissions: data.hasAllPermissions,
            sampleRoutes: data.routes?.slice(0, 5) || [],
          });
          dispatch(setPermissions(data));
          setLoading(false);
        })
        .catch((err) => {
          console.error(
            "[PermissionRoute] Error fetching user permissions:",
            err
          );
          console.error(
            "[PermissionRoute] Error details:",
            err.message,
            err.response?.data
          );
          setError("Failed to load permissions. Please try again later.");
          setLoading(false);
        });
    } else {
      console.log(
        "[PermissionRoute] Conditions NOT met for fetching permissions:",
        {
          isAuthenticated,
          permissionsLoaded: permissions.loaded,
          loading,
          role: permissions.role,
          routesCount: permissions.routes?.length || 0,
        }
      );
    }
  }, [
    isAuthenticated,
    permissions.loaded,
    dispatch,
    loading,
    permissions.role,
    permissions.routes?.length,
  ]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Error</h4>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If user is authenticated and permissions are loaded, check if they have access to this route
  if (isAuthenticated && permissions.loaded) {
    const currentPath = location.pathname;

    // Debug logging
    console.log("[PermissionRoute] Checking access for path:", currentPath);
    console.log("[PermissionRoute] User permissions:", {
      role: permissions.role,
      hasAllPermissions: permissions.hasAllPermissions,
      routes: permissions.routes,
      page_permissions: permissions.page_permissions,
    });

    // Super admin or user with all permissions can access everything
    if (permissions.hasAllPermissions || permissions.role === "super_admin") {
      console.log(
        "[PermissionRoute] User is super_admin or has all permissions"
      );

      // If he is in the root redirect him to the products page
      if (location.pathname === "/") return <Navigate to="/products/list" />;
      return children;
    }

    // Handle root path ('/') - redirect to first available permitted page
    if (currentPath === "/") {
      console.log(
        "[PermissionRoute] At root path, finding first permitted page"
      );
      const allPermittedPaths = [];

      // Collect all permitted paths from page_permissions
      if (
        permissions.page_permissions &&
        Object.keys(permissions.page_permissions).length > 0
      ) {
        allPermittedPaths.push(...Object.keys(permissions.page_permissions));
        console.log("[pages]: ", allPermittedPaths);
      }

      // Add paths from routes array if not already included
      if (permissions.routes && permissions.routes.length > 0) {
        permissions.routes.forEach((route) => {
          if (!allPermittedPaths.includes(route)) {
            allPermittedPaths.push(route);
          }
        });
      }

      if (allPermittedPaths.length > 0) {
        // Sort to ensure consistent navigation
        console.log("HERE 1: ", allPermittedPaths);

        allPermittedPaths.sort(
          (firstPage, secondPage) =>
            orderOfPage[firstPage] - orderOfPage[secondPage]
        );
        const firstPermittedPath = allPermittedPaths[0];
        console.log(
          `[PermissionRoute] Redirecting from root to first permitted path: ${firstPermittedPath}`
        );
        return <Navigate to={firstPermittedPath} replace />;
      }
    }

    // Check if user has permission to access this route using both old and new permission structures
    console.log(
      "[PermissionRoute] Available page_permissions keys:",
      Object.keys(permissions.page_permissions || {})
    );
    console.log(
      "[PermissionRoute] Available routes:",
      permissions.routes || []
    );

    // First check using the new page_permissions structure
    let hasPermissionFromPagePermissions = false;
    if (
      permissions.page_permissions &&
      Object.keys(permissions.page_permissions).length > 0
    ) {
      hasPermissionFromPagePermissions = Object.keys(
        permissions.page_permissions
      ).some((route) => {
        // Exact match
        if (route === currentPath) {
          console.log(
            `[PermissionRoute] Exact match found for ${currentPath} in page_permissions`
          );
          return true;
        }

        // Check if the current path starts with a permitted route (for nested routes)
        if (currentPath.startsWith(route)) {
          console.log(
            `[PermissionRoute] Path ${currentPath} starts with permitted route ${route} in page_permissions`
          );
          return true;
        }

        return false;
      });
    }

    // Then check using the old routes array as fallback
    let hasPermissionFromRoutes = false;
    if (permissions.routes && permissions.routes.length > 0) {
      hasPermissionFromRoutes = permissions.routes.some((route) => {
        // Exact match
        if (route === currentPath) {
          console.log(
            `[PermissionRoute] Exact match found for ${currentPath} in routes array`
          );
          return true;
        }

        // Check if the current path starts with a permitted route (for nested routes)
        if (currentPath.startsWith(route)) {
          console.log(
            `[PermissionRoute] Path ${currentPath} starts with permitted route ${route} in routes array`
          );
          return true;
        }

        return false;
      });
    }

    // User has permission if either check passes
    const hasPermission =
      hasPermissionFromPagePermissions || hasPermissionFromRoutes;
    console.log("[PermissionRoute] Permission check results:", {
      fromPagePermissions: hasPermissionFromPagePermissions,
      fromRoutes: hasPermissionFromRoutes,
      finalResult: hasPermission,
    });

    // Check if we're at a section root (e.g., /accounting) and need to redirect to a subpage
    const pathSegments = currentPath.split("/").filter(Boolean);
    if (pathSegments.length === 1 && !hasPermission) {
      // We're at a section root like /accounting, /products, etc.
      const sectionRoot = `/${pathSegments[0]}`;
      console.log(
        `[PermissionRoute] At section root: ${sectionRoot}, checking for accessible subpages`
      );

      // Find all accessible subpages within this section
      const accessibleSubpages = [];

      // Check in page_permissions first
      if (
        permissions.page_permissions &&
        Object.keys(permissions.page_permissions).length > 0
      ) {
        Object.keys(permissions.page_permissions).forEach((route) => {
          if (route.startsWith(`${sectionRoot}/`)) {
            accessibleSubpages.push(route);
          }
        });
      }

      // Then check in routes array as fallback
      if (permissions.routes && permissions.routes.length > 0) {
        permissions.routes.forEach((route) => {
          if (
            route.startsWith(`${sectionRoot}/`) &&
            !accessibleSubpages.includes(route)
          ) {
            accessibleSubpages.push(route);
          }
        });
      }

      console.log(
        `[PermissionRoute] Found ${accessibleSubpages.length} accessible subpages for ${sectionRoot}:`,
        accessibleSubpages
      );

      if (accessibleSubpages.length > 0) {
        // Sort to ensure consistent navigation to the first subpage alphabetically
        // Sort them in the order they appear in the page
        accessibleSubpages.sort(
          (subPage1, subPage2) => orderOfPage[subPage1] - orderOfPage[subPage2]
        );
        console.log("HERE 2: ", accessibleSubpages);

        const firstAccessibleSubpage = accessibleSubpages[0];
        console.log(
          `[PermissionRoute] Redirecting to first accessible subpage: ${firstAccessibleSubpage}`
        );
        console.log("[ISSUE] from here");

        return (
          <>
            {children}
            <Navigate to={firstAccessibleSubpage} replace />;
          </>
        );
      }
    }

    if (!hasPermission) {
      // Log the unauthorized access attempt
      console.warn(
        `Unauthorized access attempt to ${currentPath} by user with role ${permissions.role}`
      );

      // Find any permitted page to redirect to instead of showing error
      const allPermittedPaths = [];

      // Collect all permitted paths from page_permissions
      if (
        permissions.page_permissions &&
        Object.keys(permissions.page_permissions).length > 0
      ) {
        allPermittedPaths.push(...Object.keys(permissions.page_permissions));
      }

      // Add paths from routes array if not already included
      if (permissions.routes && permissions.routes.length > 0) {
        permissions.routes.forEach((route) => {
          if (!allPermittedPaths.includes(route)) {
            allPermittedPaths.push(route);
          }
        });
      }

      if (allPermittedPaths.length > 0) {
        // Sort to ensure consistent navigation
        allPermittedPaths.sort(
          (subPage1, subPage2) => orderOfPage[subPage1] - orderOfPage[subPage2]
        );
        const firstPermittedPath = allPermittedPaths[0];
        console.log(
          `[PermissionRoute] User lacks permission for ${currentPath}, redirecting to: ${firstPermittedPath}`
        );
        return <Navigate to={firstPermittedPath} replace />;
      }

      // If you want edit this and disable 'go back' button

      // If no permitted paths found, show error and try to navigate back
      // Swal.fire({
      //   icon: "error",
      //   title: "Access Denied",
      //   text: `You don't have permission to access ${currentPath}. Please contact your administrator if you need access to this page.`,
      //   showConfirmButton: true,
      //   confirmButtonText: "Go back",
      //   showCancelButton: false,
      //   allowOutsideClick: false,
      // });

      // When the path is / and user tried to go to / and got refuesed that means he doesn't have any permission yet
      if (location.pathname === "/" && previousPath === "/") {
        console.log("[PermissionRoute] User doesn/t have any permission yet");

        return <Navigate to="/no-permissions" />;
      }

      console.log(
        `[PermissionRoute] No permitted paths found, redirecting to previous path: ${previousPath}`
      );

      console.log(
        `[ISSUE] This is the issue man, previous path ${previousPath}`
      );

      // Navigate back to the previous path where the user had permission
      return <Navigate to={previousPath} replace />;
    }
  }

  return children;
};

export default PermissionRoute;
